/**
 * **The Przegląd's „Ostatnie sesje" table** — KANON „Karty zadań", layout A (`canvas.js`
 * `sessionsTable`), owner, 24 September 2026. Eight rows, newest first: the sessions the client
 * finished, with the plan days they MISSED woven in by date, so a gap in the training reads as a gap
 * rather than as two sessions that happen to sit far apart.
 *
 * ── Two reads, because neither can say the whole thing ───────────────────────────────────────
 * `GET /customer/{id}/recent-sessions` filters on a finished workout server-side, so a missed day
 * can never be one of its rows. The client's schedule (`/calendarevent/customer/{id}/schedule`) is
 * what knows a day was owed and not trained — the same read, and the same status rule
 * (`entities/calendar-event`), the week strips on the task cards use. A missed day on which the
 * client ALSO finished a session is not drawn as missed: the row that happened says more.
 *
 * ── Δ tonnage is against the previous run of the SAME session ────────────────────────────────
 * „Push B" this week against „Push B" last week — the canon's footnote, „Δ tonażu do poprzedniej tej
 * samej sesji planu". Against the row underneath it the number would compare a leg day with a pull
 * day and mean nothing. „The same session" is the plan label the backend resolves; a session with no
 * plan label has nothing to be the same as and carries no Δ, and neither does one whose previous run
 * is outside the rows this read returned — an unknown is never printed as a zero.
 */

import {
  calendarDayOf,
  occurrencePlacementDay,
  occurrenceStatusOf,
  occurrenceWasMissed,
  type OccurrencePlacement,
  type OccurrenceStatusSource,
} from '~/entities/calendar-event';

/** How many rows the table draws — and how many sessions the read asks for. */
export const SESSION_TABLE_ROWS = 8;

export type SessionMode = 'withTrainer' | 'solo';

/** The heading's aside — `/customer/{id}/progress-summary` over the last `PROGRESS_WINDOW_DAYS`. */
export interface ProgressSummary {
  readonly sessions: number;
  readonly personalRecords: number;
}

export const PROGRESS_WINDOW_DAYS = 30;

export interface RecentSessionSource {
  readonly workoutExecutionId: number;
  /** The session's day — a `YYYY-MM-DD`, or a stamp that starts with one. */
  readonly date: string;
  /** The session's own name — „Push Session". `null` for an ad-hoc session outside a plan. */
  readonly planLabel: string | null;
  readonly setsDone: number;
  /** Counted sets the session prescribed. Warm-ups are already excluded server-side. */
  readonly setsTotal: number;
  readonly tonnageKg: number | null;
  readonly durationMin: number | null;
  /** 1–10, 10 = best. `null` when the client logged none — distinct from rock bottom. */
  readonly mood: number | null;
  /** Hours slept the night before, as the client reported them. */
  readonly sleepHours: number | null;
  /** What the client wrote under the session, if anything. */
  readonly clientNote: string | null;
  /** The trainer's note exists. */
  readonly reviewed: boolean;
  readonly isPersonalRecord: boolean;
  readonly mode: SessionMode | null;
}

/** One occurrence of the client's schedule, as far as this table reads it. */
export interface ScheduleOccurrence extends OccurrencePlacement, OccurrenceStatusSource {
  readonly sessionName: string | null;
}

export interface SessionTableSessionRow {
  readonly kind: 'session';
  readonly key: string;
  readonly workoutExecutionId: number;
  readonly day: string;
  readonly name: string | null;
  readonly record: boolean;
  readonly mode: SessionMode | null;
  readonly setsDone: number;
  readonly setsTotal: number;
  readonly tonnageKg: number | null;
  /** Whole percent against the previous run of the same session; `null` where there is none. */
  readonly tonnageDeltaPct: number | null;
  readonly durationMin: number | null;
  readonly mood: number | null;
  readonly sleepHours: number | null;
  /** The client wrote something under this session. */
  readonly clientWrote: boolean;
  /** The trainer's own note exists. */
  readonly reviewed: boolean;
}

export interface SessionTableMissedRow {
  readonly kind: 'missed';
  readonly key: string;
  readonly day: string;
  readonly name: string | null;
}

export type SessionTableRow = SessionTableSessionRow | SessionTableMissedRow;

/**
 * The table's rows, newest first, at most `SESSION_TABLE_ROWS`.
 *
 * `todayIso` bounds the missed days: the schedule read runs to today, and a day still ahead cannot
 * have been missed whatever a stale status says.
 */
export function sessionTable(
  sessions: readonly RecentSessionSource[],
  schedule: readonly ScheduleOccurrence[],
  todayIso: string,
): readonly SessionTableRow[] {
  const dated = sessions.flatMap((session) => {
    const day = calendarDayOf(session.date);
    return day === null ? [] : [{ session, day }];
  });
  // Oldest first, so „the previous run" is simply the last one seen.
  const chronological = [...dated].sort((left, right) => left.day.localeCompare(right.day));
  const lastTonnage = new Map<string, number | null>();
  const sessionRows: SessionTableSessionRow[] = chronological.map(({ session, day }) => {
    const label = session.planLabel?.trim() ?? '';
    const previous = label === '' ? undefined : lastTonnage.get(label);
    if (label !== '') lastTonnage.set(label, session.tonnageKg);
    return {
      kind: 'session',
      key: `session-${String(session.workoutExecutionId)}`,
      workoutExecutionId: session.workoutExecutionId,
      day,
      name: label === '' ? null : label,
      record: session.isPersonalRecord,
      mode: session.mode,
      setsDone: session.setsDone,
      setsTotal: session.setsTotal,
      tonnageKg: session.tonnageKg,
      tonnageDeltaPct: deltaPct(session.tonnageKg, previous),
      durationMin: session.durationMin,
      mood: session.mood,
      sleepHours: session.sleepHours,
      clientWrote: (session.clientNote?.trim() ?? '') !== '',
      reviewed: session.reviewed,
    };
  });

  const trainedDays = new Set(sessionRows.map((row) => row.day));
  const missedRows: SessionTableMissedRow[] = schedule.flatMap((occurrence) => {
    if (!occurrenceWasMissed(occurrenceStatusOf(occurrence))) return [];
    const day = occurrencePlacementDay(occurrence, null);
    if (day === null || day > todayIso || trainedDays.has(day)) return [];
    const name = occurrence.sessionName?.trim() ?? '';
    return [{ kind: 'missed', key: `missed-${day}-${name}`, day, name: name === '' ? null : name }];
  });

  // Newest first; on one day the session that happened stands above the one that did not.
  return [...sessionRows, ...missedRows]
    .sort((left, right) =>
      left.day === right.day
        ? Number(left.kind === 'missed') - Number(right.kind === 'missed')
        : right.day.localeCompare(left.day),
    )
    .slice(0, SESSION_TABLE_ROWS);
}

function deltaPct(now: number | null, previous: number | null | undefined): number | null {
  if (now === null || previous === null || previous === undefined || previous <= 0) return null;
  return Math.round(((now - previous) / previous) * 100);
}
